import { Component, OnInit, Input, OnChanges, AfterViewInit, OnDestroy, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { UserService } from '../../../../../auth/user.service';
import { CommonUtil } from '../../../../../shared/services/utility/common.service';
import { NotificationMessageService } from '../../../../../shared/notification-message/notification-message.service';
import { CommonDataService } from '../../../../../shared/services/common-data.service';
import { DevicesDashboardService } from '../../../devices/devices-dashboard.service';
import { PlinksDashboardService } from '../plinks-dashboard.service';
import { SideMenuService } from '../../../../../shared/side-menu/side-menu.service';
import { TranslateService } from '@ngx-translate/core';
import { PolarLinkService } from '../../../../../admin/devices/polarlink/polarlink.service';

@Component({
	selector: 'app-plink-settings',
	templateUrl: './plink-settings.component.html',
	styleUrls: ['./plink-settings.component.css']
})
export class PlinkSettingsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

	@Input() customerID: number = 0;
	@Input() siteID: number = 0;
	@Input() plinksIDs: any = [];
	@Input() hasPlinks = false;
	plink: any = {};
	currentUser: any = {};
	changesStack: any = {};
	userAccess: any = {};
	plinksInfoObj: any = {};
	originalPlinksInfo: any = [];
	oldPlink: any = {};
	formName: string = '';
	tab: string = 'CARDS';
	stage: string = '';

	isBulk: boolean = false;
	currentSite: any = {};
	currentSiteSubscription: any;
	mobilePassword: boolean = false;
	actAccessPassword: boolean = false;

	@ViewChild("saveOptionsModal") saveOptionsModal;

	constructor(
		public userService: UserService,
		public commonUtil: CommonUtil,
		private elementRef: ElementRef,
		private renderer: Renderer2,
		private notificationMessage: NotificationMessageService,
		public commonData: CommonDataService,
		private devicesDashboard: DevicesDashboardService,
		private plinksDashboard: PlinksDashboardService,
		private sideMenuService: SideMenuService,
		private polarLinkService: PolarLinkService,
		private translateService: TranslateService
	) {
		devicesDashboard.settingsDeviceType = "PLINK";
	}

	ngOnInit() {
		this.currentUser = this.userService.getCurrentUser();
		this.userAccess = this.currentUser.userAccess;

		this.currentSiteSubscription = this.sideMenuService.currentSite.subscribe(data => {
			if(data && data.id)
				this.currentSite = data;
		});

		if(this.plinksIDs.length > 1) {
			this.isBulk = true;
			this.hasPlinks = true;
		}

		this.devicesDashboard.isBulkSettings = this.isBulk;
	}

	ngAfterViewInit() {
		// Initialize any modals or view children here
	}

	ngOnChanges(changes: any) {
		if(changes.plinksIDs && changes.plinksIDs.currentValue && changes.plinksIDs.currentValue.length > 0)
			this.getPlinksSettingsInfo();
	}

	ngOnDestroy() {
		if(this.currentSiteSubscription)
			this.currentSiteSubscription.unsubscribe();
	}

	setTab(tabId: string) {
		this.tab = tabId;
	}

	isSet(tabId: string) {
		return this.tab === tabId;
	}

	getPlinksSettingsInfo() {
		if(!this.plinksIDs || this.plinksIDs.length === 0) return;

		// Call backend API to get PolarLink settings info
		this.plinksDashboard.getPlinksSettingsInfo(this.plinksIDs).subscribe((data: any) => {
			if(data && data.settings && data.settings.length > 0) {
				this.plink = data.settings[0]; // For single device
				this.changesStack = data.changesStack[this.plink.id] || {};
				this.originalPlinksInfo = JSON.parse(JSON.stringify(data.settings));
				if(this.plink.data == null) this.plink.data = {};
				this.oldPlink = JSON.parse(JSON.stringify(this.plink));
			}
		}, (error) => {
			console.error('Error fetching PolarLink settings:', error);
		});
	}

	checkChanges(formName: string) {
		// Validate and save changes
		if(this.hasChanges(formName)) {
			this.saveOptionsModal.show();
		}
	}

	// Validate changes
	hasChanges(formName: string): boolean {
		this.formName = formName;
		let errorMsg = '';
		let changedFields = [];
		let changedFields2: any = {};
		let invalidFields = [];

		this.resetForms(true);

		// Validate length of field
		const checkLength = (value: any, field: string, invalidFields: any) => {
			if(value.length > fieldMapping[field].length) {
				errorMsg += `${fieldMapping[field].name} must be less than ${fieldMapping[field].length + 1} characters <br/>`;
				invalidFields.push(field);
			}
		}

		const invalidField = (value: any, field:string, invalidFields: any) => {
			errorMsg += `${fieldMapping[field].name} is invalid <br/>`
			invalidFields.push(field);
		}

		// Validate field is a number
		const isNumber = (value:any, field: string, invalidFields: any) => {
			if(isNaN(value)){
				errorMsg += `${fieldMapping[field].name} must only contain numbers <br/>`;
				invalidFields.push(field);
			}
		}

		const fieldMapping: any = {
			serialnumber: {length: 18, name: 'Serial Number', errorMessage: "is invalid"},
			mobileaccessssid: {length: 32, name: 'Mobile SSID'},
			mobileaccessssidpassword: {length: 14, name: 'Mobile SSID Password'},
			mobileport: {length: 5, name: 'Mobile SSID Port'},
			actviewip: {length: 32, name: 'ACTView IP'},
			actviewport: {length: 5, name: 'ACTView Port'},
			actviewconnectfrequency: {length: 5, name: 'ACTView Connect Frequency'},
			actaccessssid: {length: 32, name: 'ACTaccess SSID'},
			actaccessssidpassword: {length: 32, name: 'ACTaccess SSID Password'},
			rsesn: {length: 15, name: 'Polar Link Serial Number'},
			hwversion: {length: 2, name: 'Hardware Revision', errorMessage: "can only contain letters and spaces"},
			memorysignature: {length: 5, name: 'Memory Signature'}
		}

		const notToSave = ['cellulartype', 'modemmodel', 'modemfwversion', 'modemimei', 'modemvendor', 'modemtype', 'connectedband', 'networkoperator', 'rssi', 'sinr', 'radioaccesstype', 'simeid', 'simiccid', 'simimsi', 'apn_configured', 'len_apn_configured', 'registration_status', 'rf_status', 'apn_in_use', 'len_apn_in_use', 'sim_status'];
		
		// Find fields that have changed
		for(let field in this.plink){
			if(notToSave.includes(field)) continue;
			if(field !== 'tags' && field !== 'groups' && this.plink[field] !== this.oldPlink[field]) {
				changedFields.push(field);
				changedFields2[field] = this.plink[field];
			}
		}

		let otherInvalidFields = this.polarLinkService.validatePolarLinkSettings(changedFields2, this.isBulk, {}, {});

		// Validate fields
		for(let field of otherInvalidFields){
			let value = this.plink[field];
			switch(field){
				case 'mobileaccessssid':
				case 'mobileaccessssidpassword':
				case 'actviewip':
				case 'actaccessssid':
				case 'actaccessssidpassword':{
					checkLength(value, field, invalidFields);
					invalidField(value, field, invalidFields)
				}
				break;
				case 'mobileport':
				case 'actviewport':
				case 'actviewconnectfrequency':
				case 'memorysignature':{
					checkLength(value, field, invalidFields);
					isNumber(value, field, invalidFields);
				}
				break;
				case 'serialnumber':
					if(!isNaN(value))isNumber(value, field, invalidFields);
					else{
						invalidFields.push(field);
						errorMsg += `${fieldMapping[field].name} ${fieldMapping[field].errorMessage} <br/>`;
					}
					break;
				case 'hwversion':
					invalidFields.push(field);
					errorMsg += `${fieldMapping[field].name} ${fieldMapping[field].errorMessage} <br/>`;
				break;
			}
		}

		if(errorMsg){
			this.notificationMessage.setMessage(errorMsg);
			this.highlightInvalidFields(invalidFields);
		}
		if(!changedFields.length)this.notificationMessage.setMessage('No changes detected', 'info');

		return !invalidFields.length && changedFields.length > 0;
	}

	savePlinkSettings(stage = 'queue') {
		const settingsData = {
			deviceId: this.plink.deviceid,
			settings: this.plink
		};

		this.plinksDashboard.savePlinkSettings(stage,settingsData).subscribe((res: any ) => {
			if(res.success) {
				this.notificationMessage.setMessage('Settings saved successfully', 'success');
				this.oldPlink = JSON.parse(JSON.stringify(this.plink));
				this.getPlinksSettingsInfo(); // Refresh data
			} else {
				this.notificationMessage.setMessage('Error saving settings', 'danger');
			}
		});
	}

	resetForms(resetInvalidFields = false) {
		if(!resetInvalidFields){
			this.plink = JSON.parse(JSON.stringify(this.oldPlink));
		}else{
			let invalidInputs = this.elementRef.nativeElement.querySelectorAll('.invalid-input');
			invalidInputs.forEach((item: any) => {
				item.classList.remove('invalid-input');
			});
		}
	}

	getStackHTML(field: string): string {
		let stack = this.changesStack;

		if(!stack || !stack[field])
			return "";

		let returnHTML = `
				<p class='stackHTML'>
					<strong class='tcenter'>Changes Stack</strong>
				</p>
				<table class='stackHTML-table'>
					<tr><th>Value</th><th>User Name</th></tr>`;

		stack[field].forEach((item: any) => {
			returnHTML += "<tr><td style='color: white'>" + item[0] + "</td><td>" + item[1] + "</td></tr>";
		});
		
		returnHTML += "</table>";

		return returnHTML;
	}

	//Highlight input that is invalid
	highlightInvalidFields(invalidFields: any) {
		let forms = this.elementRef.nativeElement.querySelectorAll('form');
				
		forms.forEach((form: any) => {
			
			if(form.getAttribute('name') == this.formName) {
				for(let idx in form) {
					let ele = form[idx];
					if(Number.isInteger(Number(idx))) {
						if(invalidFields.includes(ele.getAttribute('name')))
							this.renderer.addClass(ele, 'invalid-input');
					}
				}
			}
		});
	}
}
